import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SearchDesktopFilters from '../Search/components/SearchDesktopFilters';
import SearchPagination from '../Search/components/SearchPagination';
import DocumentsListingSearchResultsContainer from './components/DocumentsListingSearchResultsContainer';
import { useSearchConfig } from '../Search/hooks/useSearchConfig';
import LoadingSpinner from '../utils/LoadingSpinner';
import SearchNoResults from '../Search/components/SearchNoResults';
import { useDocumentListing } from './hooks/useDocumentListing';

const DocumentsListingInner = () => {
  const config = useSearchConfig();

  const mount = document.getElementById('documents-listing-root');
  const listingType = mount?.dataset.listingType ?? 'documents';
  const prefilterCategoryIds: number[] = (() => {
    const raw = mount?.dataset.prefilterDocumentCategories;
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  })();

  const {
    results,
    isLoading,
    isError,
    isSuccess,
    facets,
    appliedFilters,
    sortOptions,
    sortOrder,
    pagination,
    toggleFacet,
    toggleFilter,
    handlePageChange,
    handleSortChange,
    activeTabKey,
  } = useDocumentListing(
    config.siteSearchApi,
    config.paginationDisplayLimit,
    config.documentContentTypes,
    prefilterCategoryIds.map(String),
    'pdfcategoryids',
    listingType
  );

  const hideFilters = prefilterCategoryIds.length === 1;
  const canShowSort = (sortOptions?.length ?? 0) > 0 && !!sortOrder && !!handleSortChange;

  const filterProps = {
    facets: hideFilters ? [] : facets,
    appliedFacets: appliedFilters,
    onToggleFacet: toggleFacet,
    onFacetItemToggle: toggleFilter,
    labels: {
      applyFilters: config.labels.applyFiltersLabel,
      clearFilters: config.labels.clearFiltersLabel,
      showingLabel: config.labels.showingResultsForLabel,
      filtersLabel: config.labels.filtersLabel,
      closeLabel: config.labels.closeLabel,
      filterByLabel: config.labels.filterByLabel,
      sortByLabel: config.labels.sortByLabel,
    },
    sortOptions,
    sortOrder,
    setSortOrder: handleSortChange,
    onClose: () => {},
  };

  const renderListingResults = () => {
    if (isLoading) {
      return (
        <div className="search__spinner-wrapper">
          <LoadingSpinner />
        </div>
      );
    }

    if (isError) {
      return (
        <SearchNoResults
          noResultsText={config.labels.noResultsText ?? 'An error occurred while fetching content. Please try again.'}
        />
      );
    }

    if (isSuccess && (!results || results.length === 0)) {
      return <SearchNoResults noResultsText={config.labels.noResultsText ?? 'No content found.'} />;
    }

    return (
      <DocumentsListingSearchResultsContainer
        results={results}
        searchTerm={''}
        lastSearchedTerm={''}
        isLoading={false}
        hasSearched={true}
        noResultsText={config.labels.noResultsText}
        activeTabKey={activeTabKey || listingType}
      />
    );
  };
  const hasFiltersOrSortOptions = !isLoading && isSuccess && results.length > 0;

  return (
    <>
      {hasFiltersOrSortOptions && canShowSort && (
        <div className="search__filters-sort-container">
          <div className="search__filters-sort-wrapper">
            <SearchDesktopFilters {...filterProps} />
          </div>
        </div>
      )}

      <div className="search__results-container content-listing__results-container--grid">{renderListingResults()}</div>

      {pagination.totalPages > 1 && (
        <div className="search__pagination-container">
          <SearchPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            prevPageLabel={config.labels.prevPageLabel}
            nextPageLabel={config.labels.nextPageLabel}
          />
        </div>
      )}
    </>
  );
};

const DocumentsListingQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

export { DocumentsListingQueryClient };

const DocumentsListing = () => {
  return (
    <QueryClientProvider client={DocumentsListingQueryClient}>
      <DocumentsListingInner />
    </QueryClientProvider>
  );
};

export default DocumentsListing;
